// Frontend sensitive keywords (mirror of backend list).
export const SENSITIVE_PLACE_KEYWORDS = [
  "hospital", "hospice", "oncology", "cancer", "abortion", "planned parenthood",
  "addiction", "methadone", "rehab", "psychiatric", "mental health",
  "fertility", "ivf", "hiv", "std", "infectious", "shelter", "domestic violence",
  "children's hospital", "pediatric oncology", "dialysis",
];

export const SENSITIVE_INDUSTRIES = new Set([
  "politics", "political", "cannabis", "cbd", "gambling", "casino",
  "mortgage", "payday", "adult", "firearms", "alcohol", "tobacco",
  "pharma", "healthcare-clinical", "crypto",
]);

export function scanAddresses(addresses) {
  const flagged = [];
  for (const a of addresses || []) {
    const s = (a || "").toLowerCase();
    const matched = SENSITIVE_PLACE_KEYWORDS.filter((kw) => s.includes(kw));
    if (matched.length) flagged.push({ address: a, matched });
  }
  return flagged;
}

// Pricing
export const CPM_RATES = {
  static: 16,
  video: 32,
  mobile: 16,
  ott_ctv: 70,
};

export const PER_FORMAT_MIN_COST = 250;
export const PER_FORMAT_MAX_COST = 50000; // $50k/mo per format
export const CAMPAIGN_MIN_COST = 500;
export const MONTHLY_BUDGET_MIN = 500;
export const SETUP_FEE = 250;
export const AD_CREATION_FEE = 300;

// Convert format budget → impressions (rounded to thousand for clean display)
export function budgetToImpressions(format, budget) {
  const cpm = CPM_RATES[format] || 0;
  if (cpm === 0 || !budget) return 0;
  return Math.round((Number(budget) / cpm) * 1000);
}

export function impressionsToBudget(format, impressions) {
  const cpm = CPM_RATES[format] || 0;
  return Math.round((Number(impressions) / 1000) * cpm * 100) / 100;
}

// Address multiplier formula. format = static|video|ott_ctv. perHH = 5|6|7
export function addressMultiplier({ addressCount, perHH, format }) {
  const impressions = (Number(addressCount) || 0) * (Number(perHH) || 0);
  const cpm = CPM_RATES[format] || 0;
  const cost = (impressions / 1000) * cpm;
  return { impressions, cost: Math.round(cost * 100) / 100, format, perHH };
}

// Geofence venue tiers
export const VENUE_TIERS = [
  { size: "small",  label: "Small (cafés, gyms, boutiques, restaurants)",     dailyTraffic: "100–500", monthlyImpressions: "15K–75K",   minImpr: 15000,  maxImpr: 75000 },
  { size: "medium", label: "Medium (dealerships, casinos, busy retail)",      dailyTraffic: "500–5,000", monthlyImpressions: "75K–750K",  minImpr: 75000,  maxImpr: 750000 },
  { size: "large",  label: "Large (stadiums, airports, festivals, conventions)", dailyTraffic: "5,000–50,000+", monthlyImpressions: "750K–5M+", minImpr: 750000, maxImpr: 5000000 },
];

// Compute pricing including base format budgets + address multiplier line items
export function calcQuote(formatsBudget, { setupFeeWaived = false, addressLines = [], adCreation = false } = {}) {
  const by_format = {};
  let media_cost = 0;
  let total_impressions = 0;
  for (const [fmt, budget] of Object.entries(formatsBudget || {})) {
    const b = Math.max(0, Number(budget) || 0);
    const cpm = CPM_RATES[fmt] || 0;
    const impressions = budgetToImpressions(fmt, b);
    by_format[fmt] = { budget: b, cpm, impressions, cost: b };
    media_cost += b;
    total_impressions += impressions;
  }
  // Address multiplier lines stack on top
  const addressItems = (addressLines || []).map((l) => {
    const item = addressMultiplier(l);
    media_cost += item.cost;
    total_impressions += item.impressions;
    return item;
  });
  const setup_fee = setupFeeWaived ? 0 : SETUP_FEE;
  const ad_creation_fee = adCreation ? AD_CREATION_FEE : 0;
  const total_cost = Math.round((media_cost + setup_fee + ad_creation_fee) * 100) / 100;
  return {
    total_impressions,
    media_cost: Math.round(media_cost * 100) / 100,
    setup_fee,
    setup_fee_waived: setupFeeWaived,
    ad_creation: !!adCreation,
    ad_creation_fee,
    total_cost,
    by_format,
    address_items: addressItems,
  };
}

export const INDUSTRIES = [
  { value: "Hospitality" }, { value: "Events & Venues" }, { value: "Automotive" },
  { value: "Legal" }, { value: "Real Estate" }, { value: "Tourism" },
  { value: "Higher Education" }, { value: "Retail" }, { value: "Restaurants" },
  { value: "Healthcare-Wellness" },
  { value: "Pharma", sensitive: true }, { value: "Politics", sensitive: true },
  { value: "Gambling", sensitive: true }, { value: "Cannabis", sensitive: true },
  { value: "Mortgage", sensitive: true }, { value: "Alcohol", sensitive: true },
  { value: "Firearms", sensitive: true }, { value: "Crypto", sensitive: true },
  { value: "Other" },
];

export const GOALS = [
  "Foot traffic to a location",
  "Online conversions / sales",
  "Brand awareness",
  "Lead generation",
  "Event attendance",
  "App installs",
];

export const DURATIONS = ["3 months", "6 months", "1 year", "2 years", "5 years", "Custom"];

export const HOUSEHOLD_OPTIONS = {
  "Household Type": ["Single", "Couple no kids", "Parents with young kids", "Parents with teens", "Empty nesters", "Multi-generational"],
  "Household Size": ["1", "2", "3-4", "5+"],
  "Home Ownership": ["Homeowner", "Renter"],
  "Home Value": ["<$300K", "$300–600K", "$600K–1M", "$1M+"],
  "Children Present": ["Yes", "No"],
  "Pet Owners": ["Dog", "Cat", "Other", "None"],
};

export const formatLabel = (k) =>
  ({ static: "Static Display", video: "Video", mobile: "Mobile", ott_ctv: "CTV / Streaming TV" }[k] || k);

// --- Guided Planner (v2) quote helpers ---
export function estimateImpressions(budget, mediaMix = "recommended", preferredFormats = []) {
  const b = Number(budget) || 0;
  if (mediaMix === "custom" && preferredFormats.length) {
    const share = b / preferredFormats.length;
    let total = 0;
    for (const f of preferredFormats) {
      const cpm = CPM_RATES[f];
      if (cpm) total += (share / cpm) * 1000;
    }
    return Math.round(total);
  }
  return Math.round((b / CPM_RATES.static) * 1000);
}

export function quoteV2(budget, { setupFeeWaived = false, adCreation = false, mediaMix = "recommended", preferredFormats = [] } = {}) {
  const media = Math.max(0, Number(budget) || 0);
  const impressions = estimateImpressions(media, mediaMix, preferredFormats);
  const setup_fee = setupFeeWaived ? 0 : SETUP_FEE;
  const ad_creation_fee = adCreation ? AD_CREATION_FEE : 0;
  return {
    media,
    impressions,
    setup_fee,
    setup_fee_waived: setupFeeWaived,
    ad_creation_fee,
    first_month_total: media + setup_fee + ad_creation_fee,
    three_month_total: media * 3 + setup_fee + ad_creation_fee,
  };
}

export const formatMoney = (n) =>
  `$${Math.round(Number(n) || 0).toLocaleString()}`;

export const formatNumber = (n) => `${(Number(n) || 0).toLocaleString()}`;
